import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { GroupedCountries, countryCodeOptions, groupedCountryCodeOptions } from 'constants/countryCodes';
import { useAuth0 } from '@auth0/auth0-react';
import LockIcon from '@mui/icons-material/Lock';
import { Button, InputAdornment, Checkbox, FormControlLabel, Switch, Tooltip, IconButton } from '@mui/material';
import _ from 'lodash';
import { toast } from 'react-toastify';
import DropdownSearchSelect, { IDropdownSelectOption } from 'components/DropdownSearchSelect/DropdownSearchSelect';
import TextInput from 'components/TextInput/TextInput';
import { getRtkErrorMessage } from 'helpers/getErrorMessage';
import { USER_SPLIT_ERROR, isValidUserSplitPercent, toUserSplitFraction } from 'helpers/ssr';
import {
  useGetAllAdminApplicationsQuery,
  useCreateClientApplicationMutation,
  useCreateAdminApplicationMutation,
} from 'reduxState/store/application/api';
import { ClientApplicationOptions } from 'reduxState/store/application/types';
import { useGetSplitPolicyOptionsQuery } from 'reduxState/store/applicationManagement/api';
import CreateAppOptions from './components/CreateAppOptions/CreateAppOptions';
import {
  applicationTypeOptions,
  createApplicationOptionItems,
  defaultCurrencyOptions,
  defaultFormData,
  defaultAppOptions,
  authTypeOptions,
  partnerUserDecisionOptions,
  PARTNER_USER_ENABLED,
  partnerUserReasonText,
  PARTNER_USER_REASON_NO_ANSWER,
} from './constants';
import { generateAdminAppOptions, generateSplitPolicyOptions, toAdminAppOption } from './helpers/helpers';

interface CreateApplicationFormData {
  applicationName: string;
  splitPolicy: IDropdownSelectOption | null;
  payoutCurrency: IDropdownSelectOption | null;
  serviceableCountries: IDropdownSelectOption | null;
  CJPID: string;
  nerfRate: string;
  productPageUrl?: string;
  authType: IDropdownSelectOption | null;
  partnerUserDecision: IDropdownSelectOption | null;
  partnerUserReason: string;
  [key: string]: CreateApplicationFormData[keyof CreateApplicationFormData];
}

const serviceableCountriesOptions = [...groupedCountryCodeOptions, ...countryCodeOptions];

/**
 * Represents a form to create a new application under an existing app group and a new app group + application. The form
 * will manually validate all required fields before submitting form. If there are any missing fields, it will display error messages
 * under the required field. If the app was created successfully, it will display a message to the user that it has been created.
 * All applications will record who created them using their email. Only WF users should have access to this form.
 *
 * @example
 * ```tsx
 *  <CreateApplication />
 * ```
 */
const CreateApplication: FC = () => {
  const [isNewPartner, setIsNewPartner] = useState(false);
  const [formData, setFormData] = useState<CreateApplicationFormData>(defaultFormData);
  const [applicationOptions, setApplicationOptions] = useState<ClientApplicationOptions>(defaultAppOptions);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [disableMatchProjection, setDisableMatchProject] = useState(true);
  const [includeCountryInAppName, setIncludeCountryInAppName] = useState(false);
  const [applicationType, setApplicationType] = useState<IDropdownSelectOption | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<IDropdownSelectOption | null>(null);
  const [entityName, setEntityName] = useState<string>('');
  const [defaultAppCountry, setDefaultAppCountry] = useState<IDropdownSelectOption | null>(null);
  const [ssrEnabled, setSsrEnabled] = useState(false);
  const [ssrSplitPercent, setSsrSplitPercent] = useState('');

  const { user } = useAuth0();
  const [createClientApplication, { isLoading: savingClientApp }] = useCreateClientApplicationMutation();
  const [createAdminApplication, { isLoading: savingAdminApp }] = useCreateAdminApplicationMutation();
  const { data: adminApplications } = useGetAllAdminApplicationsQuery();
  const companyOptions = useMemo(() => generateAdminAppOptions(adminApplications ?? []), [adminApplications]);
  // Set right before a company change that must keep the current application name; read by the name effect.
  const keepApplicationNameRef = useRef(false);
  const { data: policies } = useGetSplitPolicyOptionsQuery();

  const isSaving = savingClientApp || savingAdminApp;
  const isExtensionAppType = applicationType?.value?.includes('Extension');
  const isShoppingPortalAppType = applicationType?.value?.includes('Shopping Portal');
  // WCN and offerwall API clients don't send a partner user ID for each user; the recommended answer is No.
  const looksLikeSharedDeviceApp =
    applicationType?.value?.includes('Offerwall API') || /\bWCN\b/i.test(formData.applicationName);
  const reasonText = partnerUserReasonText[formData.partnerUserDecision?.value ?? PARTNER_USER_REASON_NO_ANSWER];
  const createAppOptionItems = isShoppingPortalAppType
    ? createApplicationOptionItems.filter(option => option.propertyTarget !== 'DefaultFeaturedMerchants')
    : createApplicationOptionItems;

  useEffect(() => {
    // Switching to a just-created company (see handleOnSubmit) must not regenerate the name the operator may
    // have edited.
    if (keepApplicationNameRef.current) {
      keepApplicationNameRef.current = false;
      return;
    }
    // When any of the required fields are cleared, existing application name should be cleared
    if ((isNewPartner && !entityName) || (!selectedCompany && !applicationType)) {
      setFormData({
        ...formData,
        applicationName: '',
      });
    }

    setFormData({
      ...formData,
      applicationName: applicationName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewPartner, selectedCompany, applicationType, entityName, includeCountryInAppName, defaultAppCountry]);

  // The dropdown matches options by reference, so once the Company list refreshes, swap a company selected in code
  // (a just-created one) for the matching option from the list.
  useEffect(() => {
    const selectedId = selectedCompany?.value?.id;
    if (selectedId === undefined) return;
    const match = companyOptions.find(option => option.value.id === selectedId);
    if (match && match !== selectedCompany) {
      keepApplicationNameRef.current = true;
      setSelectedCompany(match);
    }
  }, [companyOptions, selectedCompany]);

  useEffect(() => {
    if (!applicationType) return;

    setApplicationOptions(currentOptions => ({
      ...currentOptions,
      IsBrowserExtension: isExtensionAppType,
      ...(isShoppingPortalAppType ? { DefaultFeaturedMerchants: false } : {}),
    }));

    // Clear authType for non-extension, non-shopping portal apps
    if (!isExtensionAppType && !isShoppingPortalAppType) {
      setFormData({
        ...formData,
        applicationName,
        authType: null,
      });
    }

    // Reset SSR when switching away from an extension app type
    if (!isExtensionAppType) {
      setSsrEnabled(false);
      setSsrSplitPercent('');
      setFormErrors(prev => {
        const next = { ...prev };
        delete next.ssrSplitPercent;
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationType]);

  const onApplicationOptionChange = (option: keyof ClientApplicationOptions): void => {
    const newOptions = {
      ...applicationOptions,
      [option]: !applicationOptions[option],
    };
    setApplicationOptions(newOptions);

    // Clear the browser extension error if the user checks the Browser Extension option
    if (option === 'IsBrowserExtension' && newOptions.IsBrowserExtension && formErrors.browserExtensionRequired) {
      removeFormErrorsOnChange('browserExtensionRequired');
    }
  };

  const removeFormErrorsOnChange = (propertyString: string) => {
    const newFormErrors = { ...formErrors };
    delete newFormErrors[propertyString];
    setFormErrors(newFormErrors);
  };

  const onSearchSelectChange = (option: IDropdownSelectOption | null, propertyString: string) => {
    if (formErrors[propertyString] && option) {
      removeFormErrorsOnChange(propertyString);
    }

    setFormData({
      ...formData,
      [propertyString]: option || null,
    });
  };

  const onTextInputChange = (value: string, propertyString: string) => {
    // A spaces-only reason is still rejected on submit, so it shouldn't clear the error either
    const hasValue = propertyString === 'partnerUserReason' ? !!value.trim() : !!value;
    if (formErrors[propertyString] && hasValue) {
      removeFormErrorsOnChange(propertyString);
    }

    setFormData({
      ...formData,
      [propertyString]: value,
    });
  };

  const validateFormData = () => {
    const newFormErrors: Record<string, boolean> = {};
    for (const key in formData) {
      const value = formData[key];

      // product page url is not required
      if (key === 'productPageUrl') continue;

      if (key === 'nerfRate') {
        const nerfRateValue = Number(formData[key]);
        if (nerfRateValue === 100) continue;
        if (nerfRateValue <= 0 || nerfRateValue > 100) {
          newFormErrors[key] = true;
        }
      }

      if (!value) newFormErrors[key] = true;
    }

    // The loop above accepts a whitespace-only reason, which deviceapi rejects
    if (!formData.partnerUserReason.trim()) {
      newFormErrors.partnerUserReason = true;
    }

    if (!selectedCompany?.value) {
      newFormErrors.selectedCompany = true;
    }

    if (!applicationType) {
      newFormErrors.applicationType = true;
    }

    if (applicationType && !isExtensionAppType && !isShoppingPortalAppType) {
      delete newFormErrors.authType;
    }

    if (isNewPartner && !entityName) {
      newFormErrors.newCompanyName = true;
    }

    if (includeCountryInAppName && !defaultAppCountry) {
      newFormErrors.defaultAppCountry = true;
    }

    // Check if applicationType includes "Extension" and browser extension is not checked
    if (isExtensionAppType && !applicationOptions.IsBrowserExtension) {
      newFormErrors.browserExtensionRequired = true;
    }

    if (!formData.applicationName.endsWith('Client')) {
      newFormErrors.applicationNameInvalid = true;
    }

    if (isExtensionAppType && ssrEnabled && !isValidUserSplitPercent(ssrSplitPercent)) {
      newFormErrors.ssrSplitPercent = true;
    }

    if (!_.isEmpty(newFormErrors)) {
      setFormErrors(newFormErrors);
      return false;
    } else {
      return true;
    }
  };

  const clearForm = () => {
    setFormData(defaultFormData);
    setApplicationType(null);
    setApplicationOptions(defaultAppOptions);
    setSelectedCompany(null);
    setEntityName('');
    setDefaultAppCountry(null);
    setIncludeCountryInAppName(false);
    setSsrEnabled(false);
    setSsrSplitPercent('');
    setFormErrors({});
    keepApplicationNameRef.current = false;
  };

  const handleOnSubmit = async () => {
    if (!validateFormData()) {
      toast.error('Please fill out all required fields.');
      return;
    }

    const {
      applicationName,
      payoutCurrency,
      nerfRate,
      CJPID,
      splitPolicy,
      productPageUrl,
      authType,
      partnerUserDecision,
      partnerUserReason,
    } = formData;

    let serviceableCountriesArray = [formData.serviceableCountries?.value];
    if (formData.serviceableCountries?.value === GroupedCountries.ALL_COUNTRIES) {
      serviceableCountriesArray = [];
    }

    const calculatedNerfRate = Number(nerfRate) / 100;

    let adminAppId: number | undefined = selectedCompany?.value.id;

    const newPartnerEntityName = entityName.replace(/admin/i, '').trim();

    if (isNewPartner) {
      try {
        const { applicationId } = await createAdminApplication({
          body: {
            Name: newPartnerEntityName + ' Admin',
            EntityName: newPartnerEntityName,
            Country: defaultAppCountry?.value,
            Currency: payoutCurrency?.value,
            ModifiedAuthor: user?.email || 'Unknown author',
          },
        }).unwrap();
        adminAppId = applicationId;
        // Switch the form to the company just created, so if the client app step below fails, fixing the
        // error and resubmitting reuses this admin app instead of creating a second one.
        keepApplicationNameRef.current = true;
        setIsNewPartner(false);
        setSelectedCompany(toAdminAppOption(applicationId, `${newPartnerEntityName} Admin`));
      } catch (error) {
        toast.error(getRtkErrorMessage(error, 'Error creating admin application'));
        console.error(error);
        return;
      }
    }

    // Only set when SSR is on; validation has already checked it.
    const userSplit = isExtensionAppType && ssrEnabled ? toUserSplitFraction(ssrSplitPercent) : null;

    try {
      if (adminAppId === undefined) {
        toast.error('missing adminAppId');
        return;
      }
      await createClientApplication({
        body: {
          ModifiedAuthor: user?.email || 'Unknown author',
          EntityName: isNewPartner ? newPartnerEntityName : selectedCompany?.value.name,
          AdminAppID: adminAppId,
          ApplicationType: applicationType?.value,
          Name: applicationName.trim(),
          Country: defaultAppCountry?.value,
          Currency: payoutCurrency?.value,
          ServiceableCountries: serviceableCountriesArray,
          NerfRate: calculatedNerfRate,
          CJPID,
          PolicyName: splitPolicy?.value,
          ProductPageURL: productPageUrl || '',
          ...(isExtensionAppType || isShoppingPortalAppType ? { AuthType: authType?.value } : {}),
          ...(userSplit !== null ? { EnableSSR: true, UserSplit: userSplit } : {}),
          PartnerUserEnabled: partnerUserDecision?.value === PARTNER_USER_ENABLED,
          PartnerUserReason: partnerUserReason.trim(),
          ...applicationOptions,
        },
      }).unwrap();
    } catch (error: unknown) {
      const errorMessage = getRtkErrorMessage(error, 'Error creating client application');
      toast.error(
        isNewPartner
          ? `${newPartnerEntityName} Admin was created, but the client application wasn't: ${errorMessage}. ` +
              'The form now uses the new company, so fix the error and resubmit.'
          : errorMessage,
        { autoClose: isNewPartner ? 10000 : undefined },
      );
      console.error(error);
      return;
    }

    clearForm();

    const toastMessage = isNewPartner
      ? `Successfully created ${entityName} Admin and ${applicationName}`
      : `Successfully created ${applicationName}`;
    toast.success(toastMessage, {
      autoClose: 8000,
    });
  };

  const generateClientApplicationName = () => {
    if (!applicationType) {
      return '';
    }

    if (isNewPartner && entityName) {
      const nameParts = [entityName, applicationType?.value];
      if (includeCountryInAppName && defaultAppCountry) {
        nameParts.push(defaultAppCountry?.value);
      }
      return nameParts.concat('Client').join(' ');
    }

    if (selectedCompany?.value.name) {
      const nameParts = [selectedCompany?.value?.name, applicationType?.value];
      if (includeCountryInAppName && defaultAppCountry) {
        nameParts.push(defaultAppCountry?.value);
      }
      return nameParts.concat('Client').join(' ');
    }

    return '';
  };

  const applicationName = generateClientApplicationName();

  const getApplicationNameErrorMessage = (): string | undefined => {
    if (formErrors.applicationName) {
      return 'Please add a client application name';
    }
    if (formErrors.applicationNameInvalid) {
      return 'Application name must end with "Client"';
    }
    return undefined;
  };

  const matchProjectionLabel = () => {
    if (disableMatchProjection) {
      return (
        <Tooltip
          title={'Match Earning Projection must be enabled when the Nerf Rate is greater than zero.'}
          placement="right"
          sx={{ fontSize: '14px', padding: '0px' }}
        >
          <IconButton>
            <span className="flex items-center text-sm text-center justify-left text-montserrat">
              Match Earning Projection
              <LockIcon className="pl-2" fontSize="medium" />
            </span>
          </IconButton>
        </Tooltip>
      );
    }

    return <span className="flex items-center text-sm text-center text-montserrat ">Match Earning Projection</span>;
  };

  return (
    <div className="flex flex-col items-center flex-1">
      <div className="max-w-[930px] min-h-[500px] flex h-fit-content w-full flex-col text-montserrat bg-white rounded-md p-[25px] shadow-md ">
        <h4 className="w-full mb-[10px]">Create Application</h4>
        <div className="flex flex-col w-full">
          <DropdownSearchSelect
            label="Company"
            value={selectedCompany}
            options={[
              { label: 'Create New Company', value: 'Create New Company', group: 'New Application' },
              ...companyOptions,
            ]}
            groupBy={(option: IDropdownSelectOption) => {
              return option.group || '';
            }}
            handleChange={(option: IDropdownSelectOption | null) => {
              if (option?.value === 'Create New Company') {
                setIsNewPartner(true);
                // If creating a new company, set default auth type to null
                // and make it not required for form submission
                if (formData.authType) {
                  setFormData({
                    ...formData,
                    authType: null,
                  });
                }
              } else {
                if (isNewPartner) setIsNewPartner(false);
                if (formErrors['selectedCompany'] && option) {
                }
              }
              // Remove any existing errors for authType since it's not required
              if (formErrors.authType && !isExtensionAppType && !isShoppingPortalAppType) {
                removeFormErrorsOnChange('authType');
              }
              setSelectedCompany(option || null);
              removeFormErrorsOnChange('selectedCompany');
            }}
            hasError={formErrors.selectedCompany}
            errorMessage="Please select Company"
            placeholder="Select Option"
            noOptionsText="No Company Found"
          />
          {isNewPartner && (
            <TextInput
              label="New Company Entity Name"
              value={entityName}
              placeholderText="Enter New Company Name"
              handleChange={(value: string) => {
                if (formErrors.newCompanyName && value) {
                  removeFormErrorsOnChange('newCompanyName');
                }
                setEntityName(value);
              }}
              hasError={formErrors.newCompanyName}
              errorText={'Please add Entity Name'}
              helperText={formData.newCompanyName ? `Admin app name: "${formData.newCompanyName} Admin"` : ''}
            />
          )}
          <DropdownSearchSelect
            label="Application Type"
            value={applicationType}
            options={applicationTypeOptions}
            handleChange={(option: IDropdownSelectOption | null) => {
              if (formErrors.applicationType && option) {
                removeFormErrorsOnChange('applicationType');
              }
              // Clear browser extension error if the new type is not an Extension
              if (formErrors.browserExtensionRequired && option && !option.value.includes('Extension')) {
                removeFormErrorsOnChange('browserExtensionRequired');
              }
              setApplicationType(option || null);
            }}
            hasError={formErrors.applicationType}
            errorMessage="Please select Application Type"
            placeholder="Select Application Type"
            noOptionsText="No Application Type Found"
          />
          <TextInput
            label="Client Application Name"
            value={formData.applicationName}
            placeholderText="Enter Client Application Name"
            handleChange={(value: string) => onTextInputChange(value, 'applicationName')}
            hasError={formErrors.applicationName || formErrors.applicationNameInvalid}
            errorText={getApplicationNameErrorMessage()}
          />
          <DropdownSearchSelect
            label="Split Policy"
            value={formData.splitPolicy}
            options={generateSplitPolicyOptions(policies ?? [])}
            handleChange={(option: IDropdownSelectOption | null) => onSearchSelectChange(option, 'splitPolicy')}
            hasError={formErrors.splitPolicy}
            errorMessage="Please select Split Policy"
            placeholder="Select a Split Policy"
            noOptionsText="No Split Policy Found"
          />
          <TextInput
            label="CJPID"
            placeholderText="Enter CJPID"
            value={formData.CJPID}
            handleChange={(value: string) => onTextInputChange(value, 'CJPID')}
            hasError={formErrors.CJPID}
            errorText={formErrors.CJPID ? 'Please add a CJPID' : undefined}
          />
          <DropdownSearchSelect
            label="Payout Currency"
            value={formData.payoutCurrency}
            options={defaultCurrencyOptions}
            handleChange={(option: IDropdownSelectOption | null) => onSearchSelectChange(option, 'payoutCurrency')}
            placeholder="Select Payout Currency"
            noOptionsText="No Currency Found"
            hasError={formErrors.payoutCurrency}
            errorMessage="Please select Payout Currency"
          />
          <div className="flex flex-col items-center justify-center w-full sm:flex-row">
            <div className="w-full grow-1">
              <DropdownSearchSelect
                label="Default App Country"
                value={defaultAppCountry}
                options={countryCodeOptions}
                handleChange={(option: IDropdownSelectOption | null) => {
                  if (formErrors.defaultAppCountry && option) {
                    removeFormErrorsOnChange('defaultAppCountry');
                  }
                  setDefaultAppCountry(option || null);
                }}
                placeholder="Select Default App Country"
                noOptionsText="No Country Found"
                hasError={formErrors.defaultAppCountry}
                errorMessage="Please select Default App Country"
              />
            </div>
            <div
              className={`w-full pl-2 sm:w-[400px] ${
                applicationName && defaultAppCountry && includeCountryInAppName ? 'sm:pb-[24px]' : ''
              }`}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCountryInAppName}
                    onChange={() => {
                      setIncludeCountryInAppName(current => !current);
                    }}
                  />
                }
                label={
                  <span className="flex items-center justify-center text-sm text-center text-montserrat">
                    Include Country in App Name
                  </span>
                }
              />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center w-full sm:flex-row">
            <div className="w-full">
              <TextInput
                label="Nerf Rate in Percentages"
                placeholderText="Enter valid Nerf Rate"
                value={formData.nerfRate}
                handleChange={(value: string) => {
                  const numberValue = Number(value);
                  if (formErrors.nerfRate) {
                    removeFormErrorsOnChange('nerfRate');
                  }

                  if (numberValue >= 100) {
                    setDisableMatchProject(true);
                    setApplicationOptions({
                      ...applicationOptions,
                      MatchProjection: false,
                    });
                  } else {
                    setDisableMatchProject(false);
                    setApplicationOptions({
                      ...applicationOptions,
                      MatchProjection: true,
                    });
                  }

                  setFormData({
                    ...formData,
                    nerfRate: value,
                  });
                }}
                hasError={formErrors.nerfRate}
                errorText={'Please add a valid Nerf Rate'}
                helperText={`${formData.nerfRate}% payout`}
                inputType="number"
                inputProps={{ min: '0', max: '100' }}
                endAdornment={<InputAdornment position="end">%</InputAdornment>}
              />
            </div>
            <div className={`w-full pl-2 sm:w-[400px] sm:pb-[24px]`}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applicationOptions.MatchProjection}
                    onChange={() => {
                      onApplicationOptionChange('MatchProjection');
                    }}
                    color={disableMatchProjection ? 'error' : 'primary'}
                    disabled={disableMatchProjection}
                  />
                }
                label={matchProjectionLabel()}
              />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center w-full sm:flex-row">
            <div className="w-full grow-1">
              <DropdownSearchSelect
                label="Serviceable Countries"
                value={formData.serviceableCountries}
                options={serviceableCountriesOptions}
                groupBy={(option: IDropdownSelectOption) => {
                  return option.group || '';
                }}
                handleChange={(option: IDropdownSelectOption | null) =>
                  onSearchSelectChange(option, 'serviceableCountries')
                }
                hasError={formErrors.serviceableCountries}
                errorMessage="Please select Serviceable Country"
                placeholder="Select a Serviceable Country"
                noOptionsText="No Countries Found"
              />
            </div>
            <div className="w-full sm:pl-2 sm:w-[400px]">
              <DropdownSearchSelect
                isDisabled={!isExtensionAppType && !isShoppingPortalAppType}
                label="Auth Type"
                value={formData.authType}
                options={authTypeOptions}
                groupBy={(option: IDropdownSelectOption) => {
                  return option.group || '';
                }}
                handleChange={(option: IDropdownSelectOption | null) => {
                  onSearchSelectChange(option, 'authType');
                }}
                hasError={formErrors.authType}
                errorMessage="Please select Auth Type"
                placeholder="Select Auth Type"
                noOptionsText="No Auth Type Found"
              />
            </div>
          </div>
          <DropdownSearchSelect
            label="Partner User"
            value={formData.partnerUserDecision}
            options={partnerUserDecisionOptions}
            handleChange={(option: IDropdownSelectOption | null) => onSearchSelectChange(option, 'partnerUserDecision')}
            hasError={formErrors.partnerUserDecision}
            errorMessage="Please choose whether this app sends a partner user ID for each user"
            placeholder="Will this app send a partner user ID for each user?"
            helperMessage={
              looksLikeSharedDeviceApp
                ? "This looks like a WCN or offerwall API client. These don't send a partner user ID for each user; the recommended answer is No."
                : undefined
            }
            tooltipText="Answer Yes only if the app sends us the partner's own user ID for each user (for example their customer ID). Apps that don't, such as API, offerwall API and WCN clients, can't link users and should answer No. Only a small internal group can change this after the app is created."
          />
          <TextInput
            label={reasonText.label}
            placeholderText={reasonText.placeholder}
            value={formData.partnerUserReason}
            handleChange={(value: string) => onTextInputChange(value, 'partnerUserReason')}
            hasError={formErrors.partnerUserReason}
            errorText={formErrors.partnerUserReason ? 'Please add a reason for the partner user answer' : undefined}
          />
          <TextInput
            label="Product Page URL (Optional)"
            placeholderText="Enter Product Page URL"
            value={formData.productPageUrl}
            handleChange={(value: string) => onTextInputChange(value, 'productPageUrl')}
          />
          <CreateAppOptions
            items={createAppOptionItems}
            options={applicationOptions}
            applicationType={applicationType?.value || ''}
            handleChange={onApplicationOptionChange}
          />
          {isExtensionAppType && (
            <div className="flex flex-col w-full mt-4">
              <h6 className="text-sm font-semibold text-montserrat mb-1">SSR</h6>
              <FormControlLabel
                control={
                  <Switch
                    checked={ssrEnabled}
                    onChange={e => {
                      setSsrEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setSsrSplitPercent('');
                        removeFormErrorsOnChange('ssrSplitPercent');
                      }
                    }}
                    inputProps={{ 'data-testid': 'ssr-toggle' } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
                label={<span className="text-sm text-montserrat">Enable SSR</span>}
              />
              {ssrEnabled && (
                <TextInput
                  label="User Split %"
                  value={ssrSplitPercent}
                  placeholderText="Enter User Split Percentage"
                  handleChange={(value: string) => {
                    if (formErrors.ssrSplitPercent && isValidUserSplitPercent(value)) {
                      removeFormErrorsOnChange('ssrSplitPercent');
                    }
                    setSsrSplitPercent(value);
                  }}
                  inputType="number"
                  inputProps={{ min: '1', max: '100', step: '1' }}
                  hasError={formErrors.ssrSplitPercent}
                  errorText={USER_SPLIT_ERROR}
                  helperText="Share of the commission shown to users as cashback."
                  endAdornment={<InputAdornment position="end">%</InputAdornment>}
                />
              )}
            </div>
          )}
          <Button
            className={`min-h-[50px] max-h-[50px] p-5 text-white leading-none text-montserrat font-bold hover:bg-dark-purple mt-5 ${
              isSaving ? 'bg-light-grey border-light-purple text-light-purple ' : 'bg-purple text-white'
            } `}
            onClick={handleOnSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Creating Application...' : 'Create Application'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateApplication;
